/**
 * <s-product-gallery> — PDP media carousel.
 *
 * Shows one slide at a time using native scroll-snap. Buttons drive it through
 * invoker commands (commandfor = this element's id):
 *   command="--previous" / "--next"           — step one slide.
 *   command="--go-to" + data-index            — jump to a slide (dots).
 *
 * Required markup:
 *   [data-gallery-track]  — the horizontal scroll-snap container.
 *   [data-gallery-slide]  — each slide inside the track.
 * Optional markup:
 *   [data-gallery-prev] / [data-gallery-next] — disabled at the ends.
 *   [data-gallery-dot]                         — gets aria-current on the active slide.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track?.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.addEventListener('keydown', this.#handleKeydown, opts);

    // The gallery always opens on the product's main image; variant images follow only a picker selection.
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    return Array.from(this.querySelectorAll('[data-gallery-slide]'));
  }

  #handleCommand = (event) => {
    if (event.command === '--previous') {
      this.goTo(this.#index - 1);
    } else if (event.command === '--next') {
      this.goTo(this.#index + 1);
    } else if (event.command === '--go-to') {
      const index = Number.parseInt(event.source?.dataset.index ?? '', 10);
      if (!Number.isNaN(index)) this.goTo(index);
    }
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.#track?.contains(event.target)) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.#index + (rtl ? -1 : 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.#index + (rtl ? 1 : -1));
    }
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  goTo(index, { instant = false } = {}) {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
    const offset = target.offsetLeft - track.offsetLeft;
    track.scrollTo({ left: offset, behavior: instant ? 'instant' : this.#reducedMotion ? 'auto' : 'smooth' });
  }

  /** Jumps to the slide showing the given media id (used for variant images). */
  goToMedia(mediaId, options) {
    if (!mediaId) return;
    const slides = this.#slides;
    const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index < 0 || index === this.#index) return;

    // Load the target image immediately and jump without sliding past every lazy image in between.
    for (const img of slides[index].querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
    this.goTo(index, { instant: true, ...options });
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #update() {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    const width = track.clientWidth || 1;
    const index = Math.round(Math.abs(track.scrollLeft) / width);
    this.#index = Math.max(0, Math.min(index, slides.length - 1));

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = this.#index === 0;
    if (next instanceof HTMLButtonElement) next.disabled = this.#index === slides.length - 1;

    for (const dot of this.querySelectorAll('[data-gallery-dot]')) {
      const isActive = Number(dot.getAttribute('data-index')) === this.#index;
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
